import { autocompletion, closeBrackets, type Completion, type CompletionContext, type CompletionResult } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { markdown } from '@codemirror/lang-markdown';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { EditorState } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  drawSelection,
  keymap,
  placeholder,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view';
import { parseWikilinkInner, type NoteIndexEntry } from '@kartothek/shared';
import { tags } from '@lezer/highlight';
import { useEffect, useRef } from 'react';
import { fuzzyFilter } from '../util';

export interface EditorProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  /** Strg/Cmd+Klick auf einen Wikilink. */
  onOpenLink: (target: string) => void;
  /** Aktueller Notiz-Index für die Autovervollständigung. */
  getIndex: () => NoteIndexEntry[];
  /** Bild einfügen/hochladen; liefert den Dateinamen für `![[…]]`. */
  onUploadFile?: (file: File) => Promise<string>;
}

const wikilinkMatcher = new MatchDecorator({
  regexp: /!?\[\[[^[\]\n]+?\]\]/g,
  decoration: () => Decoration.mark({ class: 'cm-wikilink' }),
});

const wikilinkHighlighter = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = wikilinkMatcher.createDeco(view);
    }
    update(u: ViewUpdate) {
      this.decorations = wikilinkMatcher.updateDeco(u, this.decorations);
    }
  },
  { decorations: (v) => v.decorations },
);

const highlight = HighlightStyle.define([
  { tag: tags.heading1, fontSize: '1.5em', fontWeight: '700' },
  { tag: tags.heading2, fontSize: '1.3em', fontWeight: '700' },
  { tag: tags.heading3, fontSize: '1.15em', fontWeight: '700' },
  { tag: [tags.heading4, tags.heading5, tags.heading6], fontWeight: '700' },
  { tag: tags.strong, fontWeight: '700' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.strikethrough, textDecoration: 'line-through' },
  { tag: tags.link, color: 'var(--accent)' },
  { tag: tags.url, color: 'var(--muted)' },
  { tag: tags.monospace, fontFamily: 'var(--mono)', color: 'var(--code)' },
  { tag: tags.quote, color: 'var(--muted)', fontStyle: 'italic' },
  { tag: tags.processingInstruction, color: 'var(--muted)' },
  { tag: tags.meta, color: 'var(--muted)' },
]);

const theme = EditorView.theme(
  {
    '&': { color: 'var(--text)', backgroundColor: 'transparent', fontSize: '15px' },
    '.cm-content': { fontFamily: 'var(--font)', caretColor: 'var(--accent)', padding: '12px 0', lineHeight: '1.6' },
    '.cm-cursor': { borderLeftColor: 'var(--accent)' },
    '&.cm-focused': { outline: 'none' },
    '.cm-selectionBackground, &.cm-focused .cm-selectionBackground, ::selection': { backgroundColor: 'var(--selection)' },
    '.cm-wikilink': { color: 'var(--accent)', cursor: 'pointer' },
    '.cm-tooltip': { backgroundColor: 'var(--panel)', border: '1px solid var(--border)' },
    '.cm-tooltip-autocomplete > ul > li[aria-selected]': { backgroundColor: 'var(--selection)', color: 'var(--text)' },
    '.cm-completionDetail': { color: 'var(--muted)', marginLeft: '8px', fontStyle: 'normal' },
  },
  { dark: true },
);

/** Findet den Wikilink an einer Position im Dokument. */
function wikilinkAt(state: EditorState, pos: number): string | null {
  const line = state.doc.lineAt(pos);
  const re = /!?\[\[([^[\]\n]+?)\]\]/g;
  for (const m of line.text.matchAll(re)) {
    const from = line.from + m.index;
    const to = from + m[0].length;
    if (pos >= from && pos <= to) return m[1]!;
  }
  return null;
}

export function Editor(props: EditorProps) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  // Aktuelle Callbacks ohne Neuaufbau des Editors
  const cb = useRef(props);
  cb.current = props;

  useEffect(() => {
    const completeWikilink = (ctx: CompletionContext): CompletionResult | null => {
      const before = ctx.matchBefore(/!?\[\[[^[\]|#\n]*$/);
      if (!before) return null;
      const embed = before.text.startsWith('!');
      const query = before.text.slice(embed ? 3 : 2);
      const after = ctx.state.sliceDoc(ctx.pos, ctx.pos + 2);
      const close = after === ']]' ? '' : ']]';
      const options: Completion[] = fuzzyFilter(cb.current.getIndex(), query, 30).map((n) => ({
        label: n.title,
        detail: n.folder || undefined,
        apply: n.title + close,
        type: 'text',
      }));
      return { from: before.from + (embed ? 3 : 2), options, filter: false };
    };

    const state = EditorState.create({
      doc: props.value,
      extensions: [
        history(),
        drawSelection(),
        closeBrackets(),
        markdown(),
        syntaxHighlighting(highlight),
        highlightSelectionMatches(),
        EditorView.lineWrapping,
        placeholder('…'),
        wikilinkHighlighter,
        autocompletion({ override: [completeWikilink], activateOnTyping: true }),
        keymap.of([
          { key: 'Mod-s', preventDefault: true, run: () => (cb.current.onSave(), true) },
          ...defaultKeymap,
          ...historyKeymap,
          ...searchKeymap,
          indentWithTab,
        ]),
        EditorView.updateListener.of((u) => {
          if (u.docChanged) cb.current.onChange(u.state.doc.toString());
        }),
        EditorView.domEventHandlers({
          mousedown(e, v) {
            if (!(e.ctrlKey || e.metaKey)) return false;
            const pos = v.posAtCoords({ x: e.clientX, y: e.clientY });
            if (pos === null) return false;
            const inner = wikilinkAt(v.state, pos);
            if (!inner) return false;
            e.preventDefault();
            cb.current.onOpenLink(parseWikilinkInner(inner).target);
            return true;
          },
          paste(e, v) {
            const file = [...(e.clipboardData?.files ?? [])].find((f) => f.type.startsWith('image/'));
            if (!file || !cb.current.onUploadFile) return false;
            e.preventDefault();
            void insertUpload(v, file, cb.current.onUploadFile);
            return true;
          },
          drop(e, v) {
            const file = [...(e.dataTransfer?.files ?? [])][0];
            if (!file || !cb.current.onUploadFile) return false;
            e.preventDefault();
            const pos = v.posAtCoords({ x: e.clientX, y: e.clientY });
            if (pos !== null) v.dispatch({ selection: { anchor: pos } });
            void insertUpload(v, file, cb.current.onUploadFile);
            return true;
          },
        }),
        theme,
      ],
    });
    view.current = new EditorView({ state, parent: host.current! });
    view.current.focus();
    return () => view.current?.destroy();
    // Editor nur einmal pro Notiz aufbauen (key am Aufrufer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Externe Wertänderung (z. B. nach Umbenennen) übernehmen
  useEffect(() => {
    const v = view.current;
    if (v && v.state.doc.toString() !== props.value) {
      v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: props.value } });
    }
  }, [props.value]);

  return <div className="editor" ref={host} />;
}

async function insertUpload(view: EditorView, file: File, upload: (f: File) => Promise<string>) {
  try {
    const name = await upload(file);
    const text = `![[${name}]]`;
    const { from, to } = view.state.selection.main;
    view.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from + text.length } });
  } catch (e) {
    alert(e instanceof Error ? e.message : String(e));
  }
}
